import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconS24CrossCircle24Filled } from '@is24/cosma-ui-icons'
import { INVALID, VALID } from '../../../../services/domain/dropdownSelect'
import { ContentStateProperty, DropdownOption, DropdownValue, LocalizedDropdownOptions } from '../../../../@types/types'
import { InputLabel } from '../../display/labelText/LabelText'
import { useFormState } from '../../../pages/insertionPage/wizard/useFormState'
import { useIsRequiredProperty } from '../../../../services/validation/requiredFieldsUtils/useIsRequiredProperty'
import { buildDropdownOptions, getSelected, getNewStyles } from '../dropdowns/commonUtils'
import { Dropdown, DropdownItem, DropdownList, TextInput } from 'is24-corecss'
import styles from './DropdownSelect.module.css'
import { useDisableBodyScroll } from '../../hooks/useDisableBodyScroll'

export type DropdownSelectProps = {
  id: string
  name: ContentStateProperty
  label?: string
  labelClassName?: string
  ariaLabel?: string
  disabled?: boolean
  value?: DropdownValue
  noInformation?: string
  style?: React.CSSProperties
  options: DropdownOption[] | LocalizedDropdownOptions
  placeholder?: string
  showPlaceholderOption?: boolean
  allowEmpty?: boolean
  errors?: string[]
  onChange: (value: any) => void
  onBlur?: () => void
  onBlurTracking?: (value: any, isUnchanged: boolean) => void
  onFocus?: () => void
  className?: string
  'data-testid'?: string
}

const DropdownSelect = ({
  id,
  label,
  labelClassName,
  name,
  ariaLabel,
  disabled,
  value,
  noInformation,
  style,
  options,
  placeholder,
  showPlaceholderOption = true,
  allowEmpty,
  errors = [],
  onChange,
  onBlur,
  onBlurTracking,
  onFocus,
  className,
  'data-testid': dataTestId,
}: DropdownSelectProps) => {
  const { t } = useTranslation()
  const { setTouched, formState } = useFormState()
  const isFieldMandatory = useIsRequiredProperty(name)
  const labelId = `${id}-input-label`
  const triggerInputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const optionRefs = useRef<Array<HTMLLIElement | null>>([])
  const pendingFocusIndexRef = useRef<number | null>(null)

  const dropdownOptions: DropdownOption[] = useMemo(
    () => buildDropdownOptions(isFieldMandatory, options, t, noInformation),
    [isFieldMandatory, noInformation, options, t]
  )

  const hasPlaceholder = Boolean(placeholder)

  const placeholderOption = useMemo(() => {
    if (!placeholder || !showPlaceholderOption) {
      return undefined
    }

    return { value: allowEmpty ? VALID : INVALID, label: placeholder }
  }, [allowEmpty, placeholder, showPlaceholderOption])

  const selectedValue = useMemo(() => {
    if (value !== undefined && value !== null) {
      return getSelected(dropdownOptions, JSON.stringify(value)) ?? { value: null, label: null }
    }

    if (hasPlaceholder) {
      return { value: null, label: null }
    }

    return dropdownOptions[0] ?? { value: null, label: null }
  }, [dropdownOptions, hasPlaceholder, value])

  const [highlightValidation, setHighlightValidation] = useState(false)
  const [actualValue, setActualValue] = useState(selectedValue)
  const actualValueRef = useRef(selectedValue)
  const isUnchangedRef = useRef(true)
  const lastValueRef = useRef(selectedValue)
  const [open, setOpen] = useState(false)

  useDisableBodyScroll(open, { mobileOnly: true })

  const hasErrors = () => errors.length > 0 && (formState.submitCount > 0 || formState.touched[name])
  const isInvalid = (!value && highlightValidation && !allowEmpty) || hasErrors()
  const newStyles: CSSProperties = getNewStyles(style, highlightValidation, value ? [value] : undefined, isInvalid)

  const resetTriggerInputScrollPosition = useCallback(() => {
    const input = triggerInputRef.current

    if (!input) {
      return
    }

    input.scrollLeft = 0
    input.setSelectionRange?.(0, 0)
  }, [])

  const scheduleTriggerInputScrollReset = useCallback(() => {
    requestAnimationFrame(() => {
      resetTriggerInputScrollPosition()
    })
  }, [resetTriggerInputScrollPosition])

  useEffect(() => {
    if (value !== undefined && value !== null) {
      setHighlightValidation(true)
    }
    setActualValue(selectedValue)
    actualValueRef.current = selectedValue
  }, [selectedValue, value])

  useEffect(() => {
    scheduleTriggerInputScrollReset()
  }, [open, scheduleTriggerInputScrollReset, selectedValue, value])

  useEffect(() => {
    if (!open || pendingFocusIndexRef.current === null) {
      return
    }

    requestAnimationFrame(() => {
      const optionToFocus = optionRefs.current[pendingFocusIndexRef.current!]
      optionToFocus?.focus()
      pendingFocusIndexRef.current = null
    })
  }, [open])

  const trackBlur = (nextValue = actualValueRef.current) => {
    onBlur?.()

    if (lastValueRef.current?.value !== nextValue?.value) {
      onBlurTracking?.(nextValue, isUnchangedRef.current)
      isUnchangedRef.current = false
      lastValueRef.current = nextValue
    }
  }

  const onSelectChange = (newValue: string) => {
    setHighlightValidation(true)
    const selected = getSelected(dropdownOptions, newValue)
    onChange(selected)
    setActualValue(selected)
    actualValueRef.current = selected
    setTouched(name)
  }

  const onVisibleInputBlur = ({ relatedTarget }: React.FocusEvent<HTMLInputElement>) => {
    if (open) {
      return
    }

    if (relatedTarget && containerRef.current?.contains(relatedTarget as Node)) {
      return
    }

    trackBlur()
  }

  const handleOpen = () => {
    if (!disabled) {
      setOpen((previousOpen) => !previousOpen)
      scheduleTriggerInputScrollReset()
    }
  }

  const handleClose = () => {
    setOpen(false)
    trackBlur()
  }

  const handleFocus = () => {
    scheduleTriggerInputScrollReset()
    onFocus?.()
  }

  const optionOffset = placeholderOption ? 1 : 0
  const totalOptionCount = dropdownOptions.length + optionOffset

  const getFocusedOptionIndex = () => {
    if (actualValue.value === null) {
      return placeholderOption ? 0 : 0
    }

    const selectedOptionIndex = dropdownOptions.findIndex((option) => option.value === actualValue.value)

    return selectedOptionIndex >= 0 ? selectedOptionIndex + optionOffset : 0
  }

  const focusOptionAtIndex = (index: number) => {
    const boundedIndex = Math.max(0, Math.min(index, totalOptionCount - 1))
    optionRefs.current[boundedIndex]?.focus()
  }

  const openFromKeyboard = (focusIndex: number) => {
    if (disabled) {
      return
    }

    pendingFocusIndexRef.current = focusIndex
    setOpen(true)
  }

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) {
      return
    }

    const focusedOptionIndex = getFocusedOptionIndex()

    switch (event.key) {
      case 'Enter':
      case ' ':
      case 'ArrowDown':
        event.preventDefault()
        openFromKeyboard(focusedOptionIndex)
        break
      case 'ArrowUp':
        event.preventDefault()
        openFromKeyboard(focusedOptionIndex || totalOptionCount - 1)
        break
      case 'Escape':
        if (open) {
          event.preventDefault()
          handleClose()
        }
        break
      default:
        break
    }
  }

  const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLLIElement>, index: number, onSelect: () => void) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        focusOptionAtIndex(index + 1)
        break
      case 'ArrowUp':
        event.preventDefault()
        focusOptionAtIndex(index - 1)
        break
      case 'Home':
        event.preventDefault()
        focusOptionAtIndex(0)
        break
      case 'End':
        event.preventDefault()
        focusOptionAtIndex(totalOptionCount - 1)
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        onSelect()
        break
      case 'Escape':
        event.preventDefault()
        setOpen(false)
        triggerInputRef.current?.focus()
        break
      case 'Tab':
        setOpen(false)
        break
      default:
        break
    }
  }

  const selectOption = (selectedOption: DropdownOption) => {
    setHighlightValidation(true)
    onChange(selectedOption)
    setActualValue(selectedOption)
    actualValueRef.current = selectedOption
    setTouched(name)
    setOpen(false)
    triggerInputRef.current?.focus()
  }

  const selectPlaceholderOption = () => {
    if (!placeholderOption) {
      return
    }

    onSelectChange(String(placeholderOption.value))
    setOpen(false)
    triggerInputRef.current?.focus()
  }

  const dropdownValue =
    typeof actualValue.label === 'string' || typeof actualValue.label === 'number' ? String(actualValue.label) : ''
  const isVisiblePalm = `desk-hide lap-hide ${open ? '' : ' palm-hide'}`
  const classNames = className ? className : ''

  return (
    <>
      <div data-testid={`${id}-palm-backdrop`} className={`${isVisiblePalm} ${styles.palmBackdrop}`} />
      <div id={`${id}-select-container`} data-testid={dataTestId} className='select-container' ref={containerRef}>
        {label && <InputLabel id={id} labelTranslationKey={label} propertyName={name} labelClass={labelClassName} />}
        <div className={`select-input-wrapper ${styles.selectInputWrapper}`}>
          <Dropdown isOpen={open} onClose={handleClose} className='one-whole'>
            <TextInput.Root
              id={id}
              invalid={isInvalid}
              style={newStyles}
              className={classNames}
              success={Boolean(value) && !isInvalid}
              disabled={disabled}
            >
              <TextInput.Input
                ref={triggerInputRef}
                data-cy={id}
                data-testid={`${id}-select-text-input`}
                className={`${styles.triggerInput} font-ellipsis`}
                value={dropdownValue}
                placeholder={placeholder ?? ''}
                readOnly
                role='combobox'
                aria-labelledby={label ? labelId : undefined}
                aria-label={ariaLabel}
                aria-expanded={open}
                aria-haspopup='listbox'
                aria-controls={open ? `${id}-dropdown-list` : undefined}
                onClick={handleOpen}
                onBlur={onVisibleInputBlur}
                onFocus={handleFocus}
                onKeyDown={handleTriggerKeyDown}
              />
            </TextInput.Root>
            {open && (
              <DropdownList
                id={`${id}-dropdown-list`}
                className={styles.dropdownList}
                isCondensed={true}
                role='listbox'
                aria-labelledby={label ? labelId : undefined}
              >
                {placeholderOption && (
                  <DropdownItem
                    ref={(element: HTMLLIElement | null) => {
                      optionRefs.current[0] = element
                    }}
                    className={actualValue.value === null ? styles.selectedOption : undefined}
                    role='option'
                    aria-selected={actualValue.value === null}
                    onClick={selectPlaceholderOption}
                    onKeyDown={(event: React.KeyboardEvent<HTMLLIElement>) =>
                      handleOptionKeyDown(event, 0, selectPlaceholderOption)
                    }
                  >
                    {placeholderOption.label}
                  </DropdownItem>
                )}
                {dropdownOptions.map((option, index) => (
                  <DropdownItem
                    ref={(element: HTMLLIElement | null) => {
                      optionRefs.current[index + optionOffset] = element
                    }}
                    id={`${id}-option-${index}`}
                    key={`${option.value ?? 'null'}-${index}`}
                    className={actualValue.value === option.value ? styles.selectedOption : undefined}
                    role='option'
                    aria-selected={actualValue.value === option.value}
                    onClick={() => !disabled && selectOption(option)}
                    onKeyDown={(event: React.KeyboardEvent<HTMLLIElement>) =>
                      handleOptionKeyDown(event, index + optionOffset, () => selectOption(option))
                    }
                  >
                    {option.label}
                  </DropdownItem>
                ))}
              </DropdownList>
            )}
          </Dropdown>
        </div>
        {isInvalid && hasErrors() && (
          <div role='alert' className='font-error' aria-labelledby={label ? labelId : undefined}>
            <div className='caption caption--error'>
              <IconS24CrossCircle24Filled className={'caption-icon'} />
              {t(`validation.${errors[0]}`)}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default DropdownSelect
